import { useCallback } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import quotationsService from "services/quotations.service"
import { clearDeletedDeliveryDatesFromErefs, clearDeletedDeliveryDatesFromFurniture } from "helpers/quotationCache.helper"
import type { AxiosResponse } from "axios"
import downloadFileService from "services/downloadFile.service"
import { useMessage } from "hooks/message.hook"
import { buildResultCsvFileName, getResultCsvErrorMessage } from "helpers/resultCsv.helper"
import { RESULT_CSV_MESSAGES, RESULT_CSV_SAVE_FLUSH_TIMEOUT } from "helpers/resultCsv.constants"
import { useResultCsvStore } from "store/resultCsv.store"
import { waitForPendingMutations } from "store/mutationQueue.store"
import {
	buildResultPdfFileName,
	getResultPdfErrorMessage,
	ResultPdfGenerationFailedError,
	ResultPdfPollTimeoutError,
} from "helpers/resultPdf.helper"
import {
	RESULT_PDF_MESSAGES,
	RESULT_PDF_SAVE_FLUSH_TIMEOUT,
	RESULT_PDF_POLL_INTERVAL_MS,
	RESULT_PDF_POLL_TIMEOUT_MS,
} from "helpers/resultPdf.constants"
import { useResultPdfStore } from "store/resultPdf.store"
import {
	buildDetailsWordFileName,
	DetailsWordGenerationFailedError,
	DetailsWordPollTimeoutError,
	getDetailsWordErrorMessage,
} from "helpers/detailsWord.helper"
import {
	DETAILS_WORD_MESSAGES,
	DETAILS_WORD_POLL_INTERVAL_MS,
	DETAILS_WORD_POLL_TIMEOUT_MS,
	DETAILS_WORD_SAVE_FLUSH_TIMEOUT,
} from "helpers/detailsWord.constants"
import { useDetailsWordStore } from "store/detailsWord.store"
import type {
	TQuotation,
	TQuotationDetail,
	TCreateQuotationRequest,
	TQuotationComponentOptionsResponse,
	TQuotationComponentSlug,
	TQuotationDetailsWordGenerationResponse,
	TUpdateQuotationConfigurationRequest,
	TQuotationResultPdfGenerationResponse,
} from "types/quotations.type"
import type {
	TConnectFurnitureRequest,
	TMoveFurnitureRequest,
	TCreateErefRequest,
	TCreateFurnitureRequest,
	TUpdateErefRequest,
	TUpdateErefStatusRequest,
	TUpdateErefCommentRequest,
	TUpdateEcallRequest,
	TCreateEcallRequest,
	TCreateEcallElevationRequest,
	TUpdateEcallElevationsRequest,
	TAutoConnectEcallElevationRequest,
	TAutoConnectErefEcallsRequest,
	TUpdateErefConfigurationRequest,
	TUpdateFurnitureCategoryRequest,
	TUpdateFurnitureFixedPriceRequest,
	TUpdateFurnitureCountertopSeriesRequest,
	TUpdateFurnitureCountertopAttachmentsRequest,
	TUpdateFurnitureRequest,
	TSelectArbitraryModelNumberRequest,
	TLinkFurnitureCustomModelNumberRequest,
	TUpdateFurnitureConfigRequest,
	TUpdateFurnitureCommentRequest,
	TUpdateFurnitureCustomizationRequest,
	TUpdateFurnitureStatusRequest,
	TDuplicateFurnitureRequest,
	TBatchUpdateFurnitureStatusRequest,
	TBatchUpdateFurnitureRequest,
	TBatchUpdateFurnitureCategoryRequest,
	TBatchSelectArbitraryModelNumberRequest,
	TBatchUpdateFurnitureCustomModelNumberRequest,
	TBatchUpdateFurnitureConfigRequest,
	TBatchUpdateFurnitureCountertopAttachmentsRequest,
	TBatchUpdateFurnitureFixedPriceRequest,
	TBatchUpdateFurnitureCommentRequest,
	TBatchUpdateFurnitureCustomizationRequest,
	TUpdatePageRequest,
} from "types/elevationReferences.type"

export const useGetQuotations = (drawingId: number) => {
	return useQuery({
		queryKey: ["quotations", drawingId],
		queryFn: () => quotationsService.getQuotations(drawingId),
		select: ({ data }) => data as TQuotation[],
	})
}

export const useGetQuotation = (quotationId?: number) => {
	return useQuery({
		queryKey: ["quotation", quotationId],
		queryFn: () => quotationsService.getQuotation(quotationId!),
		select: ({ data }) => data as TQuotationDetail,
		enabled: !!quotationId,
	})
}

export const useGetComponentOptions = ({
	seriesId,
	components,
	quotationId,
}: {
	seriesId?: number
	components: TQuotationComponentSlug[]
	quotationId?: number
}) => {
	return useQuery({
		queryKey: ["quotationComponentOptions", seriesId, components.join(","), quotationId],
		queryFn: () =>
			quotationsService.getComponentOptions({
				series_id: seriesId!,
				components,
				quotation_id: quotationId,
			}),
		select: ({ data }) => data as TQuotationComponentOptionsResponse,
		enabled: !!seriesId && components.length > 0,
	})
}

export const useCreateQuotation = () => {
	return useMutation({
		mutationKey: ["createQuotation"],
		mutationFn: (body: TCreateQuotationRequest) => quotationsService.createQuotation(body),
	})
}

export const useUpdateQuotationConfiguration = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: ["updateQuotationConfiguration"],
		mutationFn: ({ quotationId, body }: { quotationId: number; body: TUpdateQuotationConfigurationRequest }) =>
			quotationsService.updateQuotationConfiguration(quotationId, body),
		onSuccess: (response, { quotationId }) => {
			// Unlike GET (`QuotationDetailResponse`), this PATCH's response schema
			// (`QuotationResponseWithConfiguration`) does NOT include `page_infos` / `related_objects` —
			// only the quotation's own fields plus `configuration`. Seeding the cache with the raw
			// response wholesale (as this used to do) silently wiped those two out of `TQuotationDetail`,
			// which crashed `useQuotationViewer`'s `createPageNames` memo on the next render and froze
			// the whole editor, not just whatever panel was open (#1215).
			//
			// So merge instead of replace: take every field the response actually returned (including
			// `version`, which the PATCH does bump), and keep `page_infos` / `related_objects` from
			// whatever was cached, since this response never carries them.
			queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
				["quotation", quotationId],
				(cached) => {
					if (!cached) return response as AxiosResponse<TQuotationDetail>
					const merged: AxiosResponse<TQuotationDetail> = {
						...response,
						data: { ...cached.data, ...response.data },
					}
					// A delivery date that just dropped out of `configuration.delivery_dates` was deleted
					// (the backend nulls it on every referencing eref via SET_NULL — see the helper's
					// docstring) — carry that forward onto the `related_objects` we just preserved from
					// the stale cache, or those erefs would keep showing the deleted date until something
					// else happened to refetch them.
					const oldIds = new Set(cached.data.configuration?.delivery_dates?.map((d) => d.id) ?? [])
					const newIds = new Set(merged.data.configuration?.delivery_dates?.map((d) => d.id) ?? [])
					const deletedIds = new Set([...oldIds].filter((id) => !newIds.has(id)))
					// Furniture's own fix-up reads each object's connected eref, so it has to run on the
					// erefs' ALREADY-corrected output, not independently.
					const withErefsCleared = clearDeletedDeliveryDatesFromErefs(merged, deletedIds)
					return clearDeletedDeliveryDatesFromFurniture(withErefsCleared, deletedIds)
				},
			)
		},
	})
}

export const useRenameQuotation = () => {
	return useMutation({
		mutationKey: ["renameQuotation"],
		mutationFn: ({ quotationId, name }: { quotationId: number; name: string }) =>
			quotationsService.renameQuotation(quotationId, name),
	})
}

export const useConnectFurnitureToEref = () => {
	return useMutation({
		mutationKey: ["connectFurnitureToEref"],
		mutationFn: ({
			quotationId,
			erefId,
			body,
		}: {
			quotationId: number
			erefId: number
			body: TConnectFurnitureRequest
		}) => quotationsService.connectFurnitureToEref(quotationId, erefId, body),
	})
}

export const useDisconnectFurnitureFromEref = () => {
	return useMutation({
		mutationKey: ["disconnectFurnitureFromEref"],
		mutationFn: ({
			quotationId,
			erefId,
			body,
		}: {
			quotationId: number
			erefId: number
			body: TConnectFurnitureRequest
		}) => quotationsService.disconnectFurnitureFromEref(quotationId, erefId, body),
	})
}

export const useMoveFurnitureToEref = () => {
	return useMutation({
		mutationKey: ["moveFurnitureToEref"],
		mutationFn: ({
			quotationId,
			erefId,
			body,
		}: {
			quotationId: number
			erefId: number
			body: TMoveFurnitureRequest
		}) => quotationsService.moveFurnitureToEref(quotationId, erefId, body),
	})
}

export const useUpdateErefConfiguration = () => {
	return useMutation({
		mutationKey: ["updateErefConfiguration"],
		mutationFn: ({
			quotationId,
			erefId,
			body,
		}: {
			quotationId: number
			erefId: number
			body: TUpdateErefConfigurationRequest
		}) => quotationsService.updateErefConfiguration(quotationId, erefId, body),
	})
}

export const useUpdatePage = () => {
	return useMutation({
		mutationKey: ["updatePage"],
		mutationFn: ({
			quotationId,
			pageInfoId,
			body,
		}: {
			quotationId: number
			pageInfoId: number
			body: TUpdatePageRequest
		}) => quotationsService.updatePage(quotationId, pageInfoId, body),
	})
}

export const useCreateEref = () => {
	return useMutation({
		mutationKey: ["createEref"],
		mutationFn: ({ quotationId, body }: { quotationId: number; body: TCreateErefRequest }) =>
			quotationsService.createEref(quotationId, body),
	})
}

export const useCreateFurniture = () => {
	return useMutation({
		mutationKey: ["createFurniture"],
		mutationFn: ({ quotationId, body }: { quotationId: number; body: TCreateFurnitureRequest }) =>
			quotationsService.createFurniture(quotationId, body),
	})
}

export const useUpdateEref = () => {
	return useMutation({
		mutationKey: ["updateEref"],
		mutationFn: ({ quotationId, erefId, body }: { quotationId: number; erefId: number; body: TUpdateErefRequest }) =>
			quotationsService.updateEref(quotationId, erefId, body),
	})
}

export const useUpdateFurnitureCategory = () => {
	return useMutation({
		mutationKey: ["updateFurnitureCategory"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureCategoryRequest
		}) => quotationsService.updateFurnitureCategory(quotationId, furnitureId, body),
	})
}

export const useUpdateFurnitureFixedPrice = () => {
	return useMutation({
		mutationKey: ["updateFurnitureFixedPrice"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureFixedPriceRequest
		}) => quotationsService.updateFurnitureFixedPrice(quotationId, furnitureId, body),
	})
}

export const useUpdateFurnitureCountertopSeries = () => {
	return useMutation({
		mutationKey: ["updateFurnitureCountertopSeries"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureCountertopSeriesRequest
		}) => quotationsService.updateFurnitureCountertopSeries(quotationId, furnitureId, body),
	})
}

/** REQ-449. */
export const useUpdateFurnitureCountertopAttachments = () => {
	return useMutation({
		mutationKey: ["updateFurnitureCountertopAttachments"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureCountertopAttachmentsRequest
		}) => quotationsService.updateFurnitureCountertopAttachments(quotationId, furnitureId, body),
	})
}

export const useUpdateFurniture = () => {
	return useMutation({
		mutationKey: ["updateFurniture"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureRequest
		}) => quotationsService.updateFurniture(quotationId, furnitureId, body),
	})
}

/**
 * Selects a model number from any category, moving the furniture's category to
 * match in the same request (REQ-398). See the service method for why this exists
 * alongside `useUpdateFurniture`.
 */
export const useSelectArbitraryModelNumber = () => {
	return useMutation({
		mutationKey: ["selectArbitraryModelNumber"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TSelectArbitraryModelNumberRequest
		}) => quotationsService.selectArbitraryModelNumber(quotationId, furnitureId, body),
	})
}

/**
 * Links (or clears) a custom model number on a furniture, converting it to a custom object
 * in the same request if it wasn't one already (REQ-434..437).
 */
export const useLinkFurnitureCustomModelNumber = () => {
	return useMutation({
		mutationKey: ["linkFurnitureCustomModelNumber"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TLinkFurnitureCustomModelNumberRequest
		}) => quotationsService.linkFurnitureCustomModelNumber(quotationId, furnitureId, body),
	})
}

export const useDuplicateFurniture = () => {
	return useMutation({
		mutationKey: ["duplicateFurniture"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TDuplicateFurnitureRequest
		}) => quotationsService.duplicateFurniture(quotationId, furnitureId, body),
	})
}

export const useUpdateFurnitureConfig = () => {
	return useMutation({
		mutationKey: ["updateFurnitureConfig"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureConfigRequest
		}) => quotationsService.updateFurnitureConfig(quotationId, furnitureId, body),
	})
}

export const useUpdateFurnitureComment = () => {
	return useMutation({
		mutationKey: ["updateFurnitureComment"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureCommentRequest
		}) => quotationsService.updateFurnitureComment(quotationId, furnitureId, body),
	})
}

export const useDeleteFurnitureComment = () => {
	return useMutation({
		mutationKey: ["deleteFurnitureComment"],
		mutationFn: ({
			quotationId,
			furnitureId,
			version,
		}: {
			quotationId: number
			furnitureId: number
			version: number
		}) => quotationsService.deleteFurnitureComment(quotationId, furnitureId, version),
	})
}

/** REQ-413/414 — creates or overwrites the object's single customization. */
export const useUpdateFurnitureCustomization = () => {
	return useMutation({
		mutationKey: ["updateFurnitureCustomization"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureCustomizationRequest
		}) => quotationsService.updateFurnitureCustomization(quotationId, furnitureId, body),
	})
}

export const useDeleteFurnitureCustomization = () => {
	return useMutation({
		mutationKey: ["deleteFurnitureCustomization"],
		mutationFn: ({
			quotationId,
			furnitureId,
			version,
		}: {
			quotationId: number
			furnitureId: number
			version: number
		}) => quotationsService.deleteFurnitureCustomization(quotationId, furnitureId, version),
	})
}

export const useUpdateErefComment = () => {
	return useMutation({
		mutationKey: ["updateErefComment"],
		mutationFn: ({
			quotationId,
			erefId,
			body,
		}: {
			quotationId: number
			erefId: number
			body: TUpdateErefCommentRequest
		}) => quotationsService.updateErefComment(quotationId, erefId, body),
	})
}

export const useDeleteErefComment = () => {
	return useMutation({
		mutationKey: ["deleteErefComment"],
		mutationFn: ({
			quotationId,
			erefId,
			version,
		}: {
			quotationId: number
			erefId: number
			version: number
		}) => quotationsService.deleteErefComment(quotationId, erefId, version),
	})
}

export const useUpdateFurnitureStatus = () => {
	return useMutation({
		mutationKey: ["updateFurnitureStatus"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureStatusRequest
		}) => quotationsService.updateFurnitureStatus(quotationId, furnitureId, body),
	})
}

export const useBatchUpdateFurnitureStatus = () => {
	return useMutation({
		mutationKey: ["batchUpdateFurnitureStatus"],
		mutationFn: ({ quotationId, body }: { quotationId: number; body: TBatchUpdateFurnitureStatusRequest }) =>
			quotationsService.batchUpdateFurnitureStatus(quotationId, body),
	})
}

export const useBatchDeleteFurniture = () => {
	return useMutation({
		mutationKey: ["batchDeleteFurniture"],
		mutationFn: ({ quotationId, items }: { quotationId: number; items: string[] }) =>
			quotationsService.batchDeleteFurniture(quotationId, items),
	})
}

// ---- Batch furniture edit (REQ-403) ----------------------------------------------------------
// One hook per endpoint, each taking a single `{ quotationId, body }` object. The batch sidebar
// picks which one a given field commits through; see `interactions/useBatchFurnitureEdit`.

export const useBatchUpdateFurniture = () => {
	return useMutation({
		mutationKey: ["batchUpdateFurniture"],
		mutationFn: ({ quotationId, body }: { quotationId: number; body: TBatchUpdateFurnitureRequest }) =>
			quotationsService.batchUpdateFurniture(quotationId, body),
	})
}

export const useBatchUpdateFurnitureCategory = () => {
	return useMutation({
		mutationKey: ["batchUpdateFurnitureCategory"],
		mutationFn: ({ quotationId, body }: { quotationId: number; body: TBatchUpdateFurnitureCategoryRequest }) =>
			quotationsService.batchUpdateFurnitureCategory(quotationId, body),
	})
}

export const useBatchSelectArbitraryModelNumber = () => {
	return useMutation({
		mutationKey: ["batchSelectArbitraryModelNumber"],
		mutationFn: ({ quotationId, body }: { quotationId: number; body: TBatchSelectArbitraryModelNumberRequest }) =>
			quotationsService.batchSelectArbitraryModelNumber(quotationId, body),
	})
}

/** One Custom Model Number for a whole selection (#1346) — the batch counterpart of the above. */
export const useBatchUpdateFurnitureCustomModelNumber = () => {
	return useMutation({
		mutationKey: ["batchUpdateFurnitureCustomModelNumber"],
		mutationFn: ({
			quotationId,
			body,
		}: {
			quotationId: number
			body: TBatchUpdateFurnitureCustomModelNumberRequest
		}) => quotationsService.batchUpdateFurnitureCustomModelNumber(quotationId, body),
	})
}

export const useBatchUpdateFurnitureConfig = () => {
	return useMutation({
		mutationKey: ["batchUpdateFurnitureConfig"],
		mutationFn: ({ quotationId, body }: { quotationId: number; body: TBatchUpdateFurnitureConfigRequest }) =>
			quotationsService.batchUpdateFurnitureConfig(quotationId, body),
	})
}

/** REQ-449 batch counterpart. */
export const useBatchUpdateFurnitureCountertopAttachments = () => {
	return useMutation({
		mutationKey: ["batchUpdateFurnitureCountertopAttachments"],
		mutationFn: ({
			quotationId,
			body,
		}: {
			quotationId: number
			body: TBatchUpdateFurnitureCountertopAttachmentsRequest
		}) => quotationsService.batchUpdateFurnitureCountertopAttachments(quotationId, body),
	})
}

export const useBatchUpdateFurnitureFixedPrice = () => {
	return useMutation({
		mutationKey: ["batchUpdateFurnitureFixedPrice"],
		mutationFn: ({ quotationId, body }: { quotationId: number; body: TBatchUpdateFurnitureFixedPriceRequest }) =>
			quotationsService.batchUpdateFurnitureFixedPrice(quotationId, body),
	})
}

export const useBatchUpdateFurnitureComment = () => {
	return useMutation({
		mutationKey: ["batchUpdateFurnitureComment"],
		mutationFn: ({ quotationId, body }: { quotationId: number; body: TBatchUpdateFurnitureCommentRequest }) =>
			quotationsService.batchUpdateFurnitureComment(quotationId, body),
	})
}

export const useBatchDeleteFurnitureComment = () => {
	return useMutation({
		mutationKey: ["batchDeleteFurnitureComment"],
		mutationFn: ({ quotationId, items }: { quotationId: number; items: string[] }) =>
			quotationsService.batchDeleteFurnitureComment(quotationId, items),
	})
}

export const useBatchUpdateFurnitureCustomization = () => {
	return useMutation({
		mutationKey: ["batchUpdateFurnitureCustomization"],
		mutationFn: ({ quotationId, body }: { quotationId: number; body: TBatchUpdateFurnitureCustomizationRequest }) =>
			quotationsService.batchUpdateFurnitureCustomization(quotationId, body),
	})
}

export const useBatchDeleteFurnitureCustomization = () => {
	return useMutation({
		mutationKey: ["batchDeleteFurnitureCustomization"],
		mutationFn: ({ quotationId, items }: { quotationId: number; items: string[] }) =>
			quotationsService.batchDeleteFurnitureCustomization(quotationId, items),
	})
}

export const useUpdateErefStatus = () => {
	return useMutation({
		mutationKey: ["updateErefStatus"],
		mutationFn: ({
			quotationId,
			erefId,
			body,
		}: {
			quotationId: number
			erefId: number
			body: TUpdateErefStatusRequest
		}) => quotationsService.updateErefStatus(quotationId, erefId, body),
	})
}

export const useDeleteEref = () => {
	return useMutation({
		mutationKey: ["deleteEref"],
		mutationFn: ({ quotationId, erefId }: { quotationId: number; erefId: number }) =>
			quotationsService.deleteEref(quotationId, erefId),
	})
}

export const useUpdateEcall = () => {
	return useMutation({
		mutationKey: ["updateEcall"],
		mutationFn: ({ quotationId, ecallId, body }: { quotationId: number; ecallId: number; body: TUpdateEcallRequest }) =>
			quotationsService.updateEcall(quotationId, ecallId, body),
	})
}

export const useCreateEcall = () => {
	return useMutation({
		mutationKey: ["createEcall"],
		mutationFn: ({ quotationId, body }: { quotationId: number; body: TCreateEcallRequest }) =>
			quotationsService.createEcall(quotationId, body),
	})
}

export const useDeleteEcall = () => {
	return useMutation({
		mutationKey: ["deleteEcall"],
		mutationFn: ({ quotationId, ecallId }: { quotationId: number; ecallId: number }) =>
			quotationsService.deleteEcall(quotationId, ecallId),
	})
}

export const useCreateEcallElevation = () => {
	return useMutation({
		mutationKey: ["createEcallElevation"],
		mutationFn: ({
			quotationId,
			ecallId,
			body,
		}: {
			quotationId: number
			ecallId: number
			body: TCreateEcallElevationRequest
		}) => quotationsService.createEcallElevation(quotationId, ecallId, body),
	})
}

export const useDeleteEcallElevation = () => {
	return useMutation({
		mutationKey: ["deleteEcallElevation"],
		mutationFn: ({
			quotationId,
			ecallId,
			ecallElevationId,
		}: {
			quotationId: number
			ecallId: number
			ecallElevationId: number
		}) => quotationsService.deleteEcallElevation(quotationId, ecallId, ecallElevationId),
	})
}

export const useUpdateEcallElevations = () => {
	return useMutation({
		mutationKey: ["updateEcallElevations"],
		mutationFn: ({
			quotationId,
			ecallId,
			body,
		}: {
			quotationId: number
			ecallId: number
			body: TUpdateEcallElevationsRequest
		}) => quotationsService.updateEcallElevations(quotationId, ecallId, body),
	})
}

export const useAutoConnectErefEcalls = () => {
	return useMutation({
		mutationKey: ["autoConnectErefEcalls"],
		mutationFn: ({
			quotationId,
			erefId,
			body,
		}: {
			quotationId: number
			erefId: number
			body: TAutoConnectErefEcallsRequest
		}) => quotationsService.autoConnectErefEcalls(quotationId, erefId, body),
	})
}

// The download name is composed from these rather than taken from the response, so callers pass what
// they know about the quotation; both names are optional and degrade individually.
type TDownloadResultCsvArgs = {
	quotationId: number
	quotationName: string
	drawingName?: string | null
}

// On-demand result CSV download (REQ-420). Owns the whole interaction — the save flush, the request,
// the toast lifecycle and blob-to-disk — so both surfaces that offer it (the quotations table row
// menu and the editor's burger menu) stay one-liners and behave identically. Deliberately
// self-contained: REQ-420 is a temporary mechanism, so this should be easy to delete.
//
// The busy flag lives in `resultCsv.store` rather than here: two components hold this hook at once,
// and only shared state makes "one generation at a time" true across both. Components that render
// the busy state subscribe via that store's selector hooks.
//
// No `useMutation` — deliberately. There is nothing to cache or invalidate (the response is a file,
// not server state), and this hook is held by the viewer orchestrator, which AGENTS.md keeps free of
// avoidable re-renders. Holding no reactive state at all means a download re-renders neither the
// orchestrator nor any other holder. Same shape as the download hooks in `downloadFIle.hook.ts`.
export const useDownloadQuotationResultCsv = () => {
	const { loading, resolve } = useMessage()

	const downloadResultCsv = useCallback(
		async ({ quotationId, quotationName, drawingName }: TDownloadResultCsvArgs) => {
			// Read through getState() so the guard sees the live value: the menu item is disabled from
			// the same store, but a click can still land before that paint.
			const { busyQuotationId, setBusyQuotationId } = useResultCsvStore.getState()
			if (busyQuotationId !== null) return
			setBusyQuotationId(quotationId)
			const toastId = loading(RESULT_CSV_MESSAGES.pending)

			try {
				const fileName = buildResultCsvFileName({ quotationName, drawingName })

				// The CSV is generated from stored state, so give an autosave that is already travelling a
				// moment to land. If it doesn't, the download still goes ahead — but says so, rather than
				// reporting a clean success over a file that may be a revision behind.
				const savesLanded = await waitForPendingMutations(RESULT_CSV_SAVE_FLUSH_TIMEOUT)

				const { data } = await quotationsService.getResultCsv(quotationId)
				downloadFileService.downloadBlob(data, fileName)
				resolve(
					toastId,
					savesLanded ? RESULT_CSV_MESSAGES.success : RESULT_CSV_MESSAGES.successMaybeStale,
					savesLanded ? "success" : "warning",
				)
			} catch (error) {
				resolve(toastId, getResultCsvErrorMessage(error), "error")
			} finally {
				useResultCsvStore.getState().setBusyQuotationId(null)
			}
		},
		[loading, resolve],
	)

	return { downloadResultCsv }
}

// The download name is composed from these rather than taken from the response, same as the CSV
// flow's own args type.
type TDownloadResultPdfArgs = {
	quotationId: number
	quotationName: string
	drawingName?: string | null
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Polls one PDF generation job until it reaches a terminal state, or throws
// ResultPdfPollTimeoutError once RESULT_PDF_POLL_TIMEOUT_MS has elapsed. Isolated from the hook body
// below so the poll loop's own control flow doesn't tangle with the try/catch/finally around it.
const pollResultPdfGeneration = async (
	quotationId: number,
	jobId: number,
): Promise<TQuotationResultPdfGenerationResponse> => {
	const deadline = Date.now() + RESULT_PDF_POLL_TIMEOUT_MS
	for (;;) {
		const { data } = await quotationsService.getResultPdfGenerationStatus(quotationId, jobId)
		if (data.generation_status === "success" || data.generation_status === "failed") return data
		if (Date.now() >= deadline) throw new ResultPdfPollTimeoutError()
		await sleep(RESULT_PDF_POLL_INTERVAL_MS)
	}
}

// On-demand annotated result PDF download (#1401). Same shape as useDownloadQuotationResultCsv —
// busy-guard, toast lifecycle, save flush, blob/file-to-disk, resolve success/warning/error — plus
// the async job this endpoint needs: start the build, then poll until it succeeds or fails. See
// resultPdf.helper.ts / resultPdf.constants.ts for why the error and poll-timing pieces differ from
// the CSV flow.
//
// The busy flag lives in resultPdf.store rather than here, for the same cross-surface reason the CSV
// hook's does — and in ITS OWN store, independent of resultCsv.store, since the two builds are
// unrelated backend jobs.
export const useDownloadQuotationResultPdf = () => {
	const { loading, resolve, updateProgress } = useMessage()

	const downloadResultPdf = useCallback(
		async ({ quotationId, quotationName, drawingName }: TDownloadResultPdfArgs) => {
			// Read through getState() so the guard sees the live value: the menu item is disabled from
			// the same store, but a click can still land before that paint.
			const { busyQuotationId, setBusyQuotationId } = useResultPdfStore.getState()
			if (busyQuotationId !== null) return
			setBusyQuotationId(quotationId)
			const toastId = loading(RESULT_PDF_MESSAGES.pending)

			try {
				const fileName = buildResultPdfFileName({ quotationName, drawingName })

				// The PDF is built from stored state, so give an autosave that is already travelling a
				// moment to land. If it doesn't, the download still goes ahead — but says so, rather than
				// reporting a clean success over a file that may be a revision behind.
				const savesLanded = await waitForPendingMutations(RESULT_PDF_SAVE_FLUSH_TIMEOUT)

				const { data: started } = await quotationsService.startResultPdfGeneration(quotationId)
				const result = await pollResultPdfGeneration(quotationId, started.job_id)

				if (result.generation_status === "failed" || !result.download_url) {
					throw new ResultPdfGenerationFailedError(result.error_message)
				}

				// The build is done — what's left is transferring a file that can run 100-200MB, so relabel
				// the toast for that phase (#1401 review) rather than leaving it reading "Preparing…" for
				// however long the transfer takes. Starts indeterminate (no fraction yet) and switches to a
				// real, byte-driven progress bar the moment `downloadFromUrl`'s callback reports one.
				updateProgress(toastId, undefined, RESULT_PDF_MESSAGES.downloading)
				await downloadFileService.downloadFromUrl(result.download_url, fileName, (fraction) =>
					updateProgress(toastId, fraction, RESULT_PDF_MESSAGES.downloading),
				)
				resolve(
					toastId,
					savesLanded ? RESULT_PDF_MESSAGES.success : RESULT_PDF_MESSAGES.successMaybeStale,
					savesLanded ? "success" : "warning",
				)
			} catch (error) {
				resolve(toastId, getResultPdfErrorMessage(error), "error")
			} finally {
				useResultPdfStore.getState().setBusyQuotationId(null)
			}
		},
		[loading, resolve, updateProgress],
	)

	return { downloadResultPdf }
}

export const useAutoConnectEcallElevation = () => {
	return useMutation({
		mutationKey: ["autoConnectEcallElevation"],
		mutationFn: ({
			quotationId,
			ecallId,
			ecallElevationId,
			body,
		}: {
			quotationId: number
			ecallId: number
			ecallElevationId: number
			body: TAutoConnectEcallElevationRequest
		}) => quotationsService.autoConnectEcallElevation(quotationId, ecallId, ecallElevationId, body),
	})
}

type TDownloadDetailsWordArgs = {
	quotationId: number
	quotationName: string
	drawingName?: string | null
}

// Polls one Word-file generation job until it reaches a terminal state, or throws
// DetailsWordPollTimeoutError once DETAILS_WORD_POLL_TIMEOUT_MS has elapsed. Isolated from the hook
// body below so the poll loop's own control flow doesn't tangle with the try/catch/finally around it.
const pollDetailsWordGeneration = async (
	quotationId: number,
	jobId: number,
): Promise<TQuotationDetailsWordGenerationResponse> => {
	const deadline = Date.now() + DETAILS_WORD_POLL_TIMEOUT_MS
	for (;;) {
		const { data } = await quotationsService.getDetailsWordGenerationStatus(quotationId, jobId)
		if (data.generation_status === "success" || data.generation_status === "failed") return data
		if (Date.now() >= deadline) throw new DetailsWordPollTimeoutError()
		await sleep(DETAILS_WORD_POLL_INTERVAL_MS)
	}
}

// On-demand Quotation Details Word file download (#1448). Same shape as
// useDownloadQuotationResultPdf — busy-guard, toast lifecycle, poll, blob/file-to-disk with a
// byte-driven progress bar, resolve success/error — since this endpoint is the same kind of async
// job the annotated result PDF (#1401) uses: start the build, then poll until it succeeds or fails.
//
// The busy flag lives in detailsWord.store rather than here, for the same cross-surface reason the
// CSV/PDF hooks' do — and in ITS OWN store, independent of the other two, since all three builds are
// unrelated backend jobs.
export const useDownloadQuotationDetailsWord = () => {
	const { loading, resolve, updateProgress } = useMessage()

	const downloadDetailsWord = useCallback(
		async ({ quotationId, quotationName, drawingName }: TDownloadDetailsWordArgs) => {
			// Read through getState() so the guard sees the live value: the menu item is disabled from
			// the same store, but a click can still land before that paint.
			const { busyQuotationId, setBusyQuotationId } = useDetailsWordStore.getState()
			if (busyQuotationId !== null) return
			setBusyQuotationId(quotationId)
			const toastId = loading(DETAILS_WORD_MESSAGES.pending)

			try {
				const fileName = buildDetailsWordFileName({ quotationName, drawingName })

				// The Word file is built from stored state, so give an autosave that is already travelling
				// a moment to land. If it doesn't, the download still goes ahead — but says so, rather than
				// reporting a clean success over a file that may be a revision behind.
				const savesLanded = await waitForPendingMutations(DETAILS_WORD_SAVE_FLUSH_TIMEOUT)

				const { data: started } = await quotationsService.startDetailsWordGeneration(quotationId)
				const result = await pollDetailsWordGeneration(quotationId, started.job_id)

				if (result.generation_status === "failed" || !result.download_url) {
					throw new DetailsWordGenerationFailedError(result.error_message)
				}

				// The build is done — what's left is transferring the file, so relabel the toast for that
				// phase rather than leaving it reading "Preparing…" for however long the transfer takes.
				// Starts indeterminate (no fraction yet) and switches to a real, byte-driven progress bar
				// the moment `downloadFromUrl`'s callback reports one.
				updateProgress(toastId, undefined, DETAILS_WORD_MESSAGES.downloading)
				await downloadFileService.downloadFromUrl(result.download_url, fileName, (fraction) =>
					updateProgress(toastId, fraction, DETAILS_WORD_MESSAGES.downloading),
				)
				resolve(
					toastId,
					savesLanded ? DETAILS_WORD_MESSAGES.success : DETAILS_WORD_MESSAGES.successMaybeStale,
					savesLanded ? "success" : "warning",
				)
			} catch (error) {
				resolve(toastId, getDetailsWordErrorMessage(error), "error")
			} finally {
				useDetailsWordStore.getState().setBusyQuotationId(null)
			}
		},
		[loading, resolve, updateProgress],
	)

	return { downloadDetailsWord }
}
